import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Alert,
  Button,
  Chip,
  Divider,
  Link,
} from "@mui/material";
import {
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebIcon,
  TheaterComedy as StageIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { Breadcrumbs } from "@/utils/components/Breadcrumbs";
import { useVenuesApi, useStagesApi } from "@/context/AppContext";
import type { Venue, Stage } from "api-client/models";

export const VenueDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const venuesApi = useVenuesApi();
  const stagesApi = useStagesApi();
  const [venue, setVenue] = useState<Venue | null>(null);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchVenue();
    fetchStages();
  }, [id]);

  const fetchVenue = async () => {
    try {
      setLoading(true);
      const data = await venuesApi.venuesRetrieve({ id: id! });
      setVenue(data);
      setError(null);
    } catch {
      setError("Failed to load venue. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const data = await stagesApi.stagesList();
      const allStages = data.results || [];
      // Filter stages for this venue
      setStages(allStages.filter((stage: Stage) => stage.venue === id));
    } catch {
      // Don't set error state for stages, just silently fail
      setStages([]);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" mt={4}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading venue...</Typography>
      </Stack>
    );
  }

  if (error || !venue) {
    return (
      <Box>
        <Alert severity="error">{error || "Venue not found"}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate("/venues")}>
          Back to Venues
        </Button>
      </Box>
    );
  }

  const formatAddress = () => {
    const addr = venue.address;
    if (!addr) return "No address provided";
    return [addr.street, addr.city, addr.state, addr.postal_code, addr.country]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      <Breadcrumbs
        items={[{ label: "Venues", path: "/venues" }, { label: venue.name }]}
      />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            {venue.name}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip label={venue.venueType} color="primary" size="small" />
            {!venue.isActive && (
              <Chip label="Inactive" color="error" size="small" />
            )}
          </Stack>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
          onClick={() => navigate(`/venues/${id}/edit`)}
        >
          Edit Venue
        </Button>
      </Stack>

      <Stack spacing={3}>
        {/* Basic Information */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {venue.description || "No description provided"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Stages
                </Typography>
                <Typography variant="body1">
                  {venue.stagesCount} stage
                  {Number(venue.stagesCount) !== 1 ? "s" : ""}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Capacity
                </Typography>
                <Typography variant="body1">
                  {venue.totalCapacity || "N/A"}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Contact & Location */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact & Location
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <LocationIcon fontSize="small" color="action" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">{formatAddress()}</Typography>
                </Box>
              </Stack>
              {venue.contactInfo?.phone && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <PhoneIcon fontSize="small" color="action" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Phone
                    </Typography>
                    <Typography variant="body1">
                      {venue.contactInfo.phone}
                    </Typography>
                  </Box>
                </Stack>
              )}
              {venue.contactInfo?.email && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <EmailIcon fontSize="small" color="action" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email
                    </Typography>
                    <Typography variant="body1">
                      {venue.contactInfo.email}
                    </Typography>
                  </Box>
                </Stack>
              )}
              {venue.contactInfo?.website && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <WebIcon fontSize="small" color="action" />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      Website
                    </Typography>
                    <Typography variant="body1">
                      <Link
                        href={venue.contactInfo.website}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {venue.contactInfo.website}
                      </Link>
                    </Typography>
                  </Box>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        {/* Technical Specifications */}
        {venue.technicalSpecs &&
          Object.values(venue.technicalSpecs).some(Boolean) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Technical Specifications
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Stack spacing={2}>
                  {venue.technicalSpecs.sound_system && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Sound System
                      </Typography>
                      <Typography variant="body1">
                        {venue.technicalSpecs.sound_system}
                      </Typography>
                    </Box>
                  )}
                  {venue.technicalSpecs.lighting && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Lighting
                      </Typography>
                      <Typography variant="body1">
                        {venue.technicalSpecs.lighting}
                      </Typography>
                    </Box>
                  )}
                  {venue.technicalSpecs.stage_dimensions && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Stage Dimensions
                      </Typography>
                      <Typography variant="body1">
                        {venue.technicalSpecs.stage_dimensions}
                      </Typography>
                    </Box>
                  )}
                  {venue.technicalSpecs.load_in_notes && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Load-In Notes
                      </Typography>
                      <Typography variant="body1">
                        {venue.technicalSpecs.load_in_notes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

        {/* Stages */}
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              mb={2}
            >
              <Typography variant="h6">Stages ({stages.length})</Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/stages/new?venue=${id}`)}
                disabled={stages.length >= 1}
              >
                Add Stage
              </Button>
            </Stack>
            <Divider sx={{ mb: 2 }} />

            {stages.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <StageIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  No stages added yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Add a stage to this venue to get started
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/stages/new?venue=${id}`)}
                >
                  Add First Stage
                </Button>
              </Box>
            ) : (
              <Stack spacing={2}>
                {stages.map((stage) => (
                  <Card key={stage.id} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            mb={1}
                          >
                            <StageIcon fontSize="small" color="action" />
                            <Link
                              variant="h6"
                              component={RouterLink}
                              to={`/stages/${stage.id}`}
                              underline="hover"
                              sx={{ minWidth: 0 }}
                            >
                              {stage.name}
                            </Link>
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                              label={
                                stage.stageType?.replace("_", " ") || "N/A"
                              }
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              label={`Capacity: ${stage.capacity || 0}`}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </Box>
                        <Button
                          size="small"
                          sx={{ flexShrink: 0 }}
                          onClick={() => navigate(`/stages/${stage.id}`)}
                        >
                          View Details
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                {stages.length >= 1 && (
                  <Alert severity="info">
                    This venue has reached its stage limit (1). Contact Solid
                    Slate to add more stages.
                  </Alert>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Stack direction="row" spacing={2} flexWrap="wrap" mt={3}>
        <Button variant="outlined" onClick={() => navigate("/venues")}>
          Back to Venues
        </Button>
      </Stack>
    </Box>
  );
};
